const { useCallback, useEffect } = require('react');

const { useI18n } = require('nordic/i18n');
const { useForm } = require('checkout-off-form-validation');
const { Card, CardContent } = require('@andes/card');
const { TextField } = require('@andes/textfield');
const { Checkbox } = require('@andes/checkbox');
const { Form } = require('@andes/form');
const { RadioButton, RadioGroup } = require('@andes/radio-button');
const { Typography, Title } = require('@andes/typography');
const { Dropdown, DropdownItem } = require('@andes/dropdown');
const classNames = require('classnames');

const translate = require('../../../translation');
const { UserAddressCard } = require('../Components/UserAddressCard');
const { ModalShippingAddress } = require('../Components/ModalShippingAddress');
const { FooterAction } = require('../Components/FooterAction');
const { Field, useFieldContext } = require('../Components/Form/Field');
const { ZipcodeSearch } = require('./ZipcodeSearch');
const { AddressFormContact } = require('./AddressFormContact');
const { useFormAddress } = require('./useFormAddress');
const { useAddressActions } = require('./userAddressActions');
const { useMakeAddressSchema } = require('./useMakeAddressSchema');

const NO_NUMBER = 'SN';

const linkButtonComponent = (text) => (
  <Typography size="s" color="link" component="a">
    {text}
  </Typography>
);

const residenceTypes = {
  residencial: 'residencial',
  laboral: 'laboral',
};

const AddressForm = ({ handleShippingAction }) => {
  const { i18n } = useI18n();
  const translations = translate(i18n);
  const { useWatchFields, setFieldsValue, setFieldsError, resetFieldsError } =
    useFieldContext();
  const { isMobile, formConfigs, receiverAddress } = useFormAddress();
  const {
    onReuseAddress,
    onModifyAddress,
    getReusableAddressInfoForModal,
    createAddressPayload,
  } = useAddressActions();
  const {
    cities,
    selectedAddress,
    hasMoreThanOneCity,
    isAddressReusable,
    showAddressModal,
  } = useWatchFields([
    'cities',
    'selectedAddress',
    'hasMoreThanOneCity',
    'isAddressReusable',
    'showAddressModal',
  ]);

  const schema = useMakeAddressSchema();
  const { errors, handleSubmit, registerSchema, clearError } = useForm();

  const hasNeighborhood = formConfigs?.neighborhood?.show;
  const hasEmail = formConfigs?.email?.show;
  const paddingAndesCard = isMobile ? 24 : 32;
  const formFields = registerSchema(schema);

  const handleModifyZipcode = useCallback(() => {
    handleShippingAction({ shipping_action: 'modify_zipcode' });
  }, []);

  const getStreetNumber = useCallback(
    (currentStreetNumber) =>
      currentStreetNumber === NO_NUMBER ? '' : currentStreetNumber,
    [],
  );

  const isStreetNumberDisabled = useCallback(
    (currentStreetNumber) => currentStreetNumber === NO_NUMBER,
    [],
  );

  const submitAction = (data, event) => {
    event.preventDefault();
    const payload = createAddressPayload(data);
    handleShippingAction(payload);
  };

  useEffect(() => {
    resetFieldsError([
      'zipcode',
      'selectedCity',
      'neighborhood',
      'street',
      'streetNumber',
      'floor',
      'description',
      'residenceOption',
      'name',
      'phoneNumber',
      'email',
    ]);
    setFieldsError(errors);
  }, [errors]);

  return (
    <>
      <Form onSubmit={handleSubmit(submitAction)}>
        <Card
          paddingSize={paddingAndesCard}
          shadow={isMobile && 'elevated'}
          className="mb-32"
        >
          <CardContent className="d-flex d-flex-column g-32">
            {!isAddressReusable && selectedAddress && (
              <UserAddressCard
                isMobile={isMobile}
                address={selectedAddress}
                handleAction={handleModifyZipcode}
              />
            )}

            {isAddressReusable && (
              <>
                <div className="d-flex d-flex-column g-8">
                  <ZipcodeSearch formFields={formFields} />

                  {isAddressReusable && selectedAddress && (
                    <UserAddressCard
                      isMobile={isMobile}
                      address={selectedAddress}
                    />
                  )}
                </div>
              </>
            )}

            {hasMoreThanOneCity && (
              <Field name="selectedCity">
                {({ value, field: { onChange } }) => (
                  <Dropdown
                    type="form"
                    value={value}
                    label={translations.SHIPPING_LABEL_CITY}
                    onChange={(_, city) => onChange(city)}
                  >
                    {cities?.map((city) => (
                      <DropdownItem value={city} title={city} />
                    ))}
                  </Dropdown>
                )}
              </Field>
            )}

            {hasNeighborhood && (
              <Field name="neighborhood">
                {({ value, field, error }) => (
                  <TextField
                    value={value}
                    label={translations.SHIPPING_LABEL_NEIGHBORHOOD}
                    srLabel={translations.SHIPPING_LABEL_NEIGHBORHOOD}
                    modifier={error && 'error'}
                    helper={error?.message}
                    placeholder={translations.SHIPPING_LABEL_NEIGHBORHOOD_EX}
                    {...{ ...formFields.neighborhood, ...field }}
                  />
                )}
              </Field>
            )}

            <Field name="street">
              {({ value, field, error }) => (
                <TextField
                  value={value}
                  label={translations.STREET}
                  srLabel={translations.STREET}
                  modifier={error && 'error'}
                  helper={error?.message}
                  placeholder={translations.SHIPPING_STREET_EX}
                  {...{ ...formFields.street, ...field }}
                />
              )}
            </Field>

            <div className="d-flex d-flex-column g-16">
              <Field name="streetNumber">
                {({ value, field, error }) => (
                  <TextField
                    className="number-input"
                    label={translations.SHIPPING_LABEL_NUMBER}
                    srLabel={translations.SHIPPING_LABEL_NUMBER}
                    value={getStreetNumber(value)}
                    modifier={error && 'error'}
                    helper={error?.message}
                    placeholder={translations.SHIPPING_STREET_NUMBER_EX}
                    disabled={isStreetNumberDisabled(value)}
                    {...{ ...formFields.streetNumber, ...field }}
                  >
                    <Checkbox
                      className="mr-12"
                      labelPosition="left"
                      label={translations.LABEL_NO_NUMBER}
                      srLabel={translations.LABEL_NO_NUMBER}
                      defaultChecked={value === NO_NUMBER}
                      onChange={(e) => {
                        clearError('streetNumber');
                        setFieldsValue({
                          streetNumber: e.target.checked ? NO_NUMBER : '',
                        });
                      }}
                    />
                  </TextField>
                )}
              </Field>
            </div>

            {formConfigs?.floor?.show && (
              <Field name="floor">
                {({ value, field }) => (
                  <TextField
                    value={value}
                    label={translations.SHIPPING_LABEL_FLOOR}
                    srLabel={translations.SHIPPING_LABEL_FLOOR}
                    placeholder={translations.SHIPPING_FLOOR_EX}
                    {...{ ...formFields.floor, ...field }}
                  />
                )}
              </Field>
            )}

            <Field name="description">
              {({ value, field }) => (
                <TextField
                  multiline
                  rows={3}
                  countdown={120}
                  label={translations.SHIPPING_LABEL_DESCRIPTION}
                  srLabel={translations.SHIPPING_LABEL_DESCRIPTION}
                  value={value}
                  placeholder={translations.SHIPPING_DESCRIPTION_EX}
                  {...{ ...formFields.description, ...field }}
                />
              )}
            </Field>

            <div className="d-flex d-flex-column g-8">
              <Typography size="s" type="body" color="primary">
                {translations.SHIPPING_ADDRESS_TYPE}
              </Typography>

              <Field name="residenceOption">
                {({ value, field: { onChange } }) => (
                  <RadioGroup value={value} onChange={onChange}>
                    <RadioButton
                      label={translations.RESIDENTIAL}
                      value={residenceTypes.residencial}
                    />
                    <RadioButton
                      label={translations.LABOR}
                      value={residenceTypes.laboral}
                    />
                  </RadioGroup>
                )}
              </Field>
            </div>
          </CardContent>
        </Card>

        <section className="d-flex d-flex-column g-8 mb-16">
          <Title size="xs">{translations.SHIPPING_CONTACT_TITLE}</Title>

          <Typography size="s" type="body" weight="regular" color="secondary">
            {translations.SHIPPING_CONTACT_SUBTITLE}
          </Typography>
        </section>

        <AddressFormContact
          paddingAndesCard={paddingAndesCard}
          formFields={formFields}
          hasEmail={hasEmail}
        />

        <div className={classNames({ 'text-center': isMobile }, 'mb-32')}>
          <Typography size="s" type="body" weight="regular">
            {i18n.jsx.gettext('Conoce {0}cómo cuidamos tu privacidad{0}.', {
              components: {
                0: linkButtonComponent,
              },
            })}
          </Typography>
        </div>

        <FooterAction isMobile={isMobile} isContinueActionVisible />
      </Form>

      <ModalShippingAddress
        isOpen={showAddressModal}
        userType="guest"
        address={getReusableAddressInfoForModal(receiverAddress)}
        isMobile={isMobile}
        handleFirstAction={onReuseAddress}
        handleSecondaryAction={onModifyAddress}
      />
    </>
  );
};

module.exports = { AddressForm };
